/**
 * Returns the appropriate text color for a given background based on Venuz AI foundation contrast rules.
 * Light background → dark text (--grey-1000); dark background → light text (--grey-100).
 *
 * Rules:
 * - Hue 0–40 or 188–360:
 *   - BG lightness > 65% → dark text (--grey-1000)
 *   - BG lightness ≤ 65% → light text (--grey-100)
 * - Hue 40–188 (greens, cyans, yellows — perceptually brighter, so the crossover is lower):
 *   - BG lightness > 40% → dark text (--grey-1000)
 *   - BG lightness ≤ 40% → light text (--grey-100)
 */

export function getContrastText(hue: number, lightness: number): string {
  const isWarmHue = (hue >= 0 && hue < 40) || (hue >= 188 && hue <= 360);

  if (isWarmHue) {
    return lightness > 65 ? 'var(--text-inverse)' : 'var(--text-primary)';
  } else {
    // Hue 40–188 (greens, cyans, yellows — perceptually brighter)
    return lightness > 40 ? 'var(--text-inverse)' : 'var(--text-primary)';
  }
}

/**
 * Dataviz token map: hue index (1-20) → actual hue degree
 * Tone index (1-6) → lightness: 90, 75, 60, 45, 30, 15
 */
const DATAVIZ_HUES = [0, 18, 36, 54, 72, 90, 108, 126, 144, 162, 180, 198, 216, 234, 252, 270, 288, 306, 324, 342];
const DATAVIZ_LIGHTNESS = [90, 75, 60, 45, 30, 15];

/**
 * Get contrast text color for a dataviz token like `var(--dataviz-5-3)`.
 * @param hueIndex 1-20 (dataviz hue number)
 * @param toneIndex 1-6 (dataviz tone number)
 */
export function getDatavizContrastText(hueIndex: number, toneIndex: number): string {
  // dynamic index (D9 class 3, RANGE-PROVED): `% 20` / `% 6` over fixed-length module-constant
  // arrays (DATAVIZ_HUES.length === 20, DATAVIZ_LIGHTNESS.length === 6) is always in range.
  const hue = DATAVIZ_HUES[(hueIndex - 1) % 20]!;
  const lightness = DATAVIZ_LIGHTNESS[(toneIndex - 1) % 6]!;
  return getContrastText(hue, lightness);
}

/**
 * Parse an HSL string like "hsl(239, 79%, 55%)" and return contrast text color.
 */
export function getContrastTextFromHSL(hslString: string): string {
  const match = hslString.match(/hsl\(\s*(\d+)/);
  const lMatch = hslString.match(/(\d+)%\s*\)/);
  if (!match || !lMatch) return 'var(--text-primary)';
  // literal index (D9 class 2): match/lMatch are non-null (checked above) and their regexes'
  // sole capture group (`(\d+)`) is required, so group 1 is always present when the match itself
  // succeeded.
  const hue = parseInt(match[1]!, 10);
  const lightness = parseInt(lMatch[1]!, 10);
  return getContrastText(hue, lightness);
}
